import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CountryMark, ServiceMark } from '../components/sms/SmsMarks';
import { ActiveNumberItem, DashboardData, NumberStatus } from '../types/models';
import { formatDateTime, formatUsd } from '../utils/format';
import { presentPhoneNumber } from '../utils/phone';

interface DashboardProps {
  data: DashboardData | null;
  activeNumbers: ActiveNumberItem[];
}

const FINAL_STATUSES = new Set<NumberStatus>(['finished', 'cancelled', 'expired', 'not_found']);

function statusLabel(status: NumberStatus): string {
  if (status === 'received') return 'СМС получено';
  if (status === 'waiting_retry') return 'Повторный запрос';
  if (status === 'waiting_resend') return 'Ожидаем новое СМС';
  if (status === 'allocated' || status === 'waiting_sms') return 'Ожидаем СМС';
  if (status === 'finished') return 'Завершён';
  if (status === 'cancelled') return 'Отменён';
  if (status === 'expired') return 'Истёк';
  return 'Не найден';
}

function latestSms(item: ActiveNumberItem): { text: string; code: string | null } | null {
  const messages = item.receivedSmsMessages || [];
  const message = messages.length > 0 ? messages[messages.length - 1] : null;
  if (message) return { text: message.text, code: message.code || null };
  if (item.smsCode) return { text: `Код подтверждения: ${item.smsCode}`, code: item.smsCode };
  return null;
}

const Dashboard: React.FC<DashboardProps> = ({ data, activeNumbers }) => {
  const currentNumbers = useMemo(() => activeNumbers
    .filter((item) => (
      !FINAL_STATUSES.has(item.status)
      && new Date(item.expiresAt).getTime() > Date.now()
    ))
    .sort((left, right) => (
      new Date(right.purchasedAt).getTime() - new Date(left.purchasedAt).getTime()
    )), [activeNumbers]);
  const latestNumbers = currentNumbers.slice(0, 5);

  if (!data) {
    return (
      <section className="sms-card sms-emptyState">
        <div className="sms-sectionHead">
          <h2>Сводка временно недоступна</h2>
          <p>Повторите обновление, чтобы загрузить показатели кабинета.</p>
        </div>
      </section>
    );
  }

  const { summary, latestOperations } = data;

  return (
    <div className="sms-pageStack">
      <header className="sms-pageIntro">
        <div>
          <h1>Главная</h1>
          <p>Баланс, активные номера и последние события — без лишних переходов.</p>
        </div>
        <Link className="u2-btn is-primary" to="/buy-number">Быстрая аренда</Link>
      </header>

      <section className="sms-kpiGrid" aria-label="Сводные показатели">
        <article className="sms-kpiCard is-balance"><span className="sms-kpiIcon" aria-hidden="true">USD</span><div className="sms-kpiLabel">Баланс</div><div className="sms-kpiValue">{formatUsd(summary.balance)}</div><div className="sms-kpiHint">Доступно для покупок</div></article>
        <article className="sms-kpiCard is-info"><span className="sms-kpiIcon" aria-hidden="true">НОМ</span><div className="sms-kpiLabel">Активные номера</div><div className="sms-kpiValue">{summary.activeNumbers}</div><div className="sms-kpiHint">Сейчас в работе</div></article>
        <article className="sms-kpiCard is-warning"><span className="sms-kpiIcon" aria-hidden="true">СМС</span><div className="sms-kpiLabel">Ожидают СМС</div><div className="sms-kpiValue">{summary.waitingSms}</div><div className="sms-kpiHint">Сообщение ещё не пришло</div></article>
        <article className="sms-kpiCard is-accent"><span className="sms-kpiIcon" aria-hidden="true">24Ч</span><div className="sms-kpiLabel">Траты за сегодня</div><div className="sms-kpiValue">{formatUsd(summary.spentToday)}</div><div className="sms-kpiHint">Расчёт ведётся по UTC</div></article>
      </section>

      <section className="sms-card sms-recentNumbers">
        <div className="sms-sectionToolbar">
          <div className="sms-sectionHead"><h2>Последние активные номера</h2><p>СМС отображается прямо в списке сразу после получения.</p></div>
          {currentNumbers.length > 5 ? <Link className="sms-textLink" to="/active-numbers">Все активные номера</Link> : null}
        </div>
        {latestNumbers.length === 0 ? (
          <div className="sms-emptyState is-compact"><strong>Активных номеров пока нет</strong><span>После покупки номер и входящее СМС появятся здесь.</span><Link className="sms-textLink" to="/buy-number">Перейти к покупке</Link></div>
        ) : (
          <div className="sms-tableWrap">
            <table className="sms-table sms-activePreviewTable">
              <thead><tr><th>Номер</th><th>Сервис и страна</th><th>Куплен</th><th>СМС</th><th>Статус</th></tr></thead>
              <tbody>{latestNumbers.map((item) => {
                const sms = latestSms(item);
                const countryTitle = item.countryName || item.countryCode;
                const serviceTitle = item.serviceName || item.serviceCode;
                return (
                  <tr key={item.id}>
                    <td data-label="Номер" className="sms-numberCell">{presentPhoneNumber(item.phoneNumber).displayFullNumber}</td>
                    <td data-label="Сервис и страна"><span className="sms-entityPair"><ServiceMark code={item.serviceCode} name={serviceTitle} /><span><strong>{serviceTitle}</strong><small><CountryMark name={countryTitle} />{countryTitle}</small></span></span></td>
                    <td data-label="Куплен">{formatDateTime(item.purchasedAt)}</td>
                    <td data-label="СМС" className="sms-messageCell">{sms ? <span className="sms-messagePreview"><span>{sms.text}</span>{sms.code ? <strong>Код: {sms.code}</strong> : null}</span> : <span className="sms-messageWaiting">Ожидаем СМС</span>}</td>
                    <td data-label="Статус"><span className={`sms-status is-${item.status}`}>{statusLabel(item.status)}</span></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </section>

      <section className="sms-card">
        <div className="sms-sectionToolbar"><div className="sms-sectionHead"><h2>Последние операции</h2><p>Списания и возвраты по вашему балансу.</p></div><Link className="sms-textLink" to="/operations">Вся история</Link></div>
        <div className="sms-tableWrap"><table className="sms-table"><thead><tr><th>Описание</th><th>Дата</th><th>Сумма</th><th>Статус</th></tr></thead><tbody>
          {latestOperations.length === 0 ? <tr><td colSpan={4} className="u2-muted">Операций пока нет.</td></tr> : latestOperations.slice(0, 5).map((item) => (
            <tr key={item.id}><td data-label="Описание">{item.description}</td><td data-label="Дата">{formatDateTime(item.date)}</td><td data-label="Сумма" className={item.direction === 'credit' ? 'is-positive' : 'is-negative'}>{item.direction === 'credit' ? '+' : '-'}{formatUsd(Math.abs(item.amount))}</td><td data-label="Статус"><span className={`sms-status ${item.status === 'done' ? 'is-done' : 'is-pending'}`}>{item.status === 'done' ? 'Выполнено' : 'Ожидание'}</span></td></tr>
          ))}
        </tbody></table></div>
      </section>
    </div>
  );
};

export default Dashboard;
